import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, HelpCircle, TrendingUp, DollarSign, Target, Users, Building, Shield, Calendar, ClipboardList, Plus, Trash2, Briefcase, Download } from 'lucide-react';
import HelpModal from './HelpModal';
import { formatPercentage, formatCurrency, getScoreColor, getScoreBgColor } from '../../utils/enhancedDashboardUtils';
import { BUSINESS_CATEGORY_KEY_MAP, BUSINESS_CATEGORY_DESCRIPTIONS } from '../../utils/categoryDescriptions';
import { useBusinessContext } from '../../contexts/BusinessContext';

const BusinessEnvisioning = ({ data, assessmentData, wealthGap, valueGap, profitGap, profileData, onSave, onProfileSave, saving }) => {
  const navigate = useNavigate();
  const { currentBusinessId } = useBusinessContext();

  // Debug logging

  // Calculate gap scores from assessment data
  const calculateBusinessGap = () => {
    if (!assessmentData || !assessmentData.category_scores) return 0;
    const categoryScores = assessmentData.category_scores;
    const businessCategories = [
      categoryScores.financial_performance,
      categoryScores.revenue_quality,
      categoryScores.customer_concentration,
      categoryScores.management_team,
      categoryScores.competitive_position,
      categoryScores.growth_potential,
      categoryScores.intellectual_property,
      categoryScores.legal_compliance,
      categoryScores.owner_dependency,
      categoryScores.strategic_positioning
    ];
    const validScores = businessCategories.filter(score => score != null && score > 0);
    return validScores.length > 0 ? (validScores.reduce((a, b) => a + b, 0) / validScores.length) : 0;
  };

  const calculateFamilyEnterpriseGap = () => {
    if (!assessmentData || !assessmentData.category_scores) return 0;
    const categoryScores = assessmentData.category_scores;
    const familyCategories = [
      categoryScores.boundary_management,
      categoryScores.family_council,
      categoryScores.equity_planning,
      categoryScores.wealth_transfer,
      categoryScores.family_governing,
      categoryScores.family_conflict,
      categoryScores.values_mission,
      categoryScores.succession_planning,
      categoryScores.employment_policies,
      categoryScores.education_development
    ];
    const validScores = familyCategories.filter(score => score != null && score > 0);
    return validScores.length > 0 ? (validScores.reduce((a, b) => a + b, 0) / validScores.length) : 0;
  };

  // Calculate overall combined gap score
  const calculateOverallGapScore = () => {
    const businessGap = calculateBusinessGap();
    const familyGap = calculateFamilyEnterpriseGap();

    // If both have scores, average them. Otherwise return whichever has a score
    if (businessGap > 0 && familyGap > 0) {
      return (businessGap + familyGap) / 2;
    }
    return businessGap > 0 ? businessGap : familyGap;
  };

  // Get top strengths and bottom risks from category scores
  const getTopStrengthsAndRisks = () => {
    if (!assessmentData || !assessmentData.category_scores) {
      return { strengths: [], risks: [] };
    }

    const categoryScores = assessmentData.category_scores;

    // Convert to array of {name, score, summary} objects
    const categoriesArray = Object.keys(BUSINESS_CATEGORY_KEY_MAP)
      .map(key => {
        const displayName = BUSINESS_CATEGORY_KEY_MAP[key];
        const description = BUSINESS_CATEGORY_DESCRIPTIONS[displayName];
        return {
          name: displayName,
          score: categoryScores[key] || 0,
          summary: description?.summary || ''
        };
      })
      .filter(cat => cat.score > 0); // Only include categories with scores

    // Sort by score
    const sortedByScore = [...categoriesArray].sort((a, b) => b.score - a.score);

    // Top 3 are strengths, bottom 3 are risks
    const strengths = sortedByScore.slice(0, 3);
    const risks = sortedByScore.slice(-3).reverse();

    return { strengths, risks };
  };

  const { strengths: autoStrengths, risks: autoRisks } = getTopStrengthsAndRisks();

  // Calculate 4C Intangible Asset Scores from assessment data
  const calculate4CScores = () => {
    if (!assessmentData || !assessmentData.responses || assessmentData.responses.length === 0) {
      return { Human: 0, Structural: 0, Customer: 0, Social: 0 };
    }

    // ID to Question ID mapping (matching useAssessmentFilters.js)
    const idToQuestionId = {
      1: 'FIN-001', 2: 'FIN-002', 3: 'FIN-003', 4: 'FIN-004', 5: 'FIN-005',
      6: 'FIN-006', 7: 'FIN-007', 8: 'FIN-008', 9: 'FIN-009', 10: 'FIN-010',
      11: 'REV-001', 12: 'REV-002', 13: 'REV-003', 14: 'REV-004', 15: 'REV-005',
      16: 'REV-006', 17: 'REV-007', 18: 'REV-008', 19: 'REV-009', 20: 'CUST-001',
      21: 'CUST-002', 22: 'CUST-003', 23: 'CUST-004', 24: 'CUST-005', 25: 'CUST-006',
      26: 'CUST-007', 27: 'CUST-008', 28: 'MGT-001', 29: 'MGT-002', 30: 'MGT-003',
      31: 'MGT-004', 32: 'MGT-005', 33: 'MGT-006', 34: 'MGT-007', 35: 'MGT-008',
      36: 'MGT-009', 37: 'MGT-010', 38: 'COMP-001', 39: 'COMP-002', 40: 'COMP-003',
      41: 'COMP-004', 42: 'COMP-005', 43: 'COMP-006', 44: 'COMP-007', 45: 'COMP-008',
      46: 'GROW-001', 47: 'GROW-002', 48: 'GROW-003', 49: 'GROW-004', 50: 'GROW-005',
      51: 'GROW-006', 52: 'GROW-007', 53: 'GROW-008', 54: 'GROW-009', 55: 'IP-001',
      56: 'IP-002', 57: 'IP-003', 58: 'IP-004', 59: 'IP-005', 60: 'IP-006',
      61: 'IP-007', 62: 'IP-008', 63: 'LEGAL-001', 64: 'LEGAL-002', 65: 'LEGAL-003',
      66: 'LEGAL-004', 67: 'LEGAL-005', 68: 'LEGAL-006', 69: 'LEGAL-007', 70: 'LEGAL-008',
      71: 'LEGAL-009', 72: 'OWNER-001', 73: 'OWNER-002', 74: 'OWNER-003', 75: 'OWNER-004',
      76: 'OWNER-005', 77: 'OWNER-006', 78: 'OWNER-007', 79: 'OWNER-008', 80: 'OWNER-009',
      81: 'OWNER-010', 82: 'STRAT-001', 83: 'STRAT-002', 84: 'STRAT-003', 85: 'STRAT-004',
      86: 'STRAT-005', 87: 'STRAT-006', 88: 'STRAT-007', 89: 'STRAT-008'
    };

    const convertIdsToQuestionIds = (numericIds) => {
      return numericIds.map(id => idToQuestionId[id]);
    };

    // Intangible Asset Mapping (matching useAssessmentFilters.js)
    const intangibleAssetMapping = {
      'Human': [
        ...convertIdsToQuestionIds([28, 30, 75, 33, 74, 35, 31, 80, 62, 76, 32, 29, 67]),
        // Family Enterprise Questions
        'BOARD-004', 'BOARD-006', 'DEV-001', 'DEV-002', 'DEV-003', 'DEV-004',
        'DEV-005', 'DEV-006', 'DEV-007', 'DEV-008', 'EMP-002', 'EMP-003',
        'EMP-004', 'EMP-006', 'EMP-007', 'EMP-008', 'GOV-008', 'SUCC-002',
        'SUCC-003', 'SUCC-004', 'SUCC-006', 'SUCC-007', 'SUCC-008', 'VALUES-002',
        'VALUES-003', 'VALUES-005', 'VALUES-006', 'VALUES-008'
      ],
      'Structural': [
        ...convertIdsToQuestionIds([55, 66, 58, 81, 54, 79, 48, 37, 60, 47, 19, 57, 14, 16, 5, 56, 50, 12, 83, 10, 69, 42, 36, 70, 61, 53, 59, 34, 7, 64]),
        // Family Enterprise Questions
        'BOARD-001', 'BOARD-002', 'BOARD-003', 'BOARD-005', 'BOARD-007', 'BOARD-008',
        'EMP-001', 'EMP-005', 'EQUITY-001', 'EQUITY-002', 'EQUITY-003', 'EQUITY-004',
        'EQUITY-006', 'EQUITY-007', 'GOV-001', 'GOV-002', 'GOV-004', 'GOV-005',
        'GOV-006', 'GOV-007', 'RES-002', 'SUCC-001', 'SUCC-005', 'VALUES-001',
        'VALUES-004', 'VALUES-007', 'WTRANS-001', 'WTRANS-002', 'WTRANS-004',
        'WTRANS-006', 'WTRANS-007'
      ],
      'Customer': convertIdsToQuestionIds([26, 15, 24, 20, 23, 21, 27, 78, 86, 25, 22, 73]),
      'Social': [
        ...convertIdsToQuestionIds([88, 43, 40, 84, 82, 87, 38, 41, 85, 44, 89, 45, 39]),
        // Family Enterprise Questions
        'BOUND-001', 'BOUND-002', 'BOUND-003', 'BOUND-004', 'BOUND-005', 'BOUND-006',
        'BOUND-007', 'GOV-003', 'RES-001', 'RES-003', 'RES-004', 'RES-005',
        'RES-006', 'RES-007', 'RES-008'
      ]
    };

    const scores = {};
    const answeredQuestions = assessmentData.responses;

    Object.keys(intangibleAssetMapping).forEach(assetType => {
      const questionIds = intangibleAssetMapping[assetType];

      // Match questions by question_id
      const matchingResponses = answeredQuestions.filter(q => {
        return questionIds.includes(q.question_id);
      });

      // Calculate average score, excluding N/A (0) responses
      const validResponses = matchingResponses.filter(r => r.answer_value !== 0);
      if (validResponses.length > 0) {
        const totalScore = validResponses.reduce((sum, r) => sum + (r.score || 0), 0);
        scores[assetType] = totalScore / validResponses.length;
      } else {
        scores[assetType] = 0;
      }
    });

    return scores;
  };

  // Convert percentage score (0-100) to 1-10 rating
  const convertScoreTo10 = (score) => {
    if (score === 0) return 0;
    return Math.max(1, Math.min(10, Math.ceil(score / 10)));
  };

  const intangibleScores = calculate4CScores();

  const gapScores = {
    overallGapScore: calculateOverallGapScore(),
    businessAssessmentGap: calculateBusinessGap(),
    familyEnterpriseGap: calculateFamilyEnterpriseGap(),
    attractivenessGap: assessmentData?.attractiveness_score || 0,
    readinessGap: assessmentData?.readiness_score || 0
  };

  const [formData, setFormData] = useState({
    // Section A: Current Position
    top_strengths: [],
    top_risks_gaps: [],
    business_model_summary: '',

    // Section B: Three Gaps
    value_gap_current: '',
    value_gap_target: '',
    value_gap_amount: '',
    wealth_gap_need: '',
    wealth_gap_current: '',
    wealth_gap_amount: '',
    profit_gap_required: '',
    profit_gap_current: '',
    profit_gap_amount: '',

    // Section C: Four Capitals (4Cs)
    human_capital_rating: '',
    human_capital_vision: '',
    structural_capital_rating: '',
    structural_capital_vision: '',
    customer_capital_rating: '',
    customer_capital_vision: '',
    social_capital_rating: '',
    social_capital_vision: '',

    // Section D: Purpose and Vision
    purpose_statement: '',
    vision_statement: '',
    mission_statement: '',
    reason_to_believe: '',
    proof_points: '',
    tone_manner: '',
    kingdom_impact_goals: '',
    core_value_1: '',
    core_value_1_applied: '',
    core_value_2: '',
    core_value_2_applied: '',
    core_value_3: '',
    core_value_3_applied: '',
    core_value_4: '',
    core_value_4_applied: '',
    core_value_5: '',
    core_value_5_applied: '',
    core_value_6: '',
    core_value_6_applied: '',
    core_value_7: '',
    core_value_7_applied: '',
    core_value_8: '',
    core_value_8_applied: '',
    core_value_9: '',
    core_value_9_applied: '',
    core_value_10: '',
    core_value_10_applied: '',
    business_owner_type: '',

    // Section E: 10-Year Kingdom Vision
    ten_year_size: '',
    ten_year_profitability: '',
    ten_year_business_value: '',
    ten_year_owner_role: '',
    ten_year_exit_options: [],

    // Section F: 3-5 Year Business Picture
    target_customers: '',
    market_reputation: '',
    competitive_position: '',
    three_year_revenue: '',
    three_year_ebitda_margin: '',
    three_year_recurring_revenue_pct: '',
    three_year_value_min: '',
    three_year_value_max: '',
    swot_strengths: '',
    swot_weaknesses: '',
    swot_opportunities: '',
    swot_threats: '',

    // Section G: Owner Role & Dependency
    responsibilities_keep: [],
    responsibilities_delegate: '',
    sabbatical_readiness: '',
    key_person_risk: '',

    // Section H: Five Ds Preparedness
    five_ds_death_items: [],
    five_ds_death: '',
    five_ds_disability_items: [],
    five_ds_disability: '',
    five_ds_divorce_items: [],
    five_ds_divorce: '',
    five_ds_distress_items: [],
    five_ds_distress: '',
    five_ds_disagreement_items: [],
    five_ds_disagreement: '',
    five_ds_priorities: '',

    // Section I: Ideal Exit Scenario
    exit_timing_window: '',
    exit_path_preferences: [],
    exit_deal_structure: '',
    exit_transition_role: '',
    exit_red_lines: '',

    // Section J: Constraints, Risks & Assumptions
    constraints: ['', '', ''],
    key_assumptions: '',
    acceleration_factors: '',
    delay_factors: '',

    // Section K: 90-Day Strategic Focus
    quick_wins: ['', '', ''],
    longer_term_projects: ['', '', ''],
    value_acceleration_priorities: '',

    // Section L: Advisory Team
    advisory_team_current: '',
    advisory_team_gaps: ''
  });

  const [saveStatus, setSaveStatus] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [helpModal, setHelpModal] = useState({ isOpen: false, title: '', content: null });

  // Advisory Team state (from business profile)
  const [advisoryTeam, setAdvisoryTeam] = useState({
    has_attorney: false, attorney_name: '', attorney_email: '', attorney_phone: '',
    has_accountant: false, accountant_name: '', accountant_email: '', accountant_phone: '',
    has_financial_advisor: false, financial_advisor_name: '', financial_advisor_email: '', financial_advisor_phone: '',
    has_exit_advisor: false, exit_advisor_name: '', exit_advisor_email: '', exit_advisor_phone: '',
    has_insurance_agent: false, insurance_agent_name: '', insurance_agent_email: '', insurance_agent_phone: '',
    has_banker: false, banker_name: '', banker_email: '', banker_phone: '',
    has_estate_planner: false, estate_planner_name: '', estate_planner_email: '', estate_planner_phone: '',
    has_business_coach: false, business_coach_name: '', business_coach_email: '', business_coach_phone: '',
    custom_advisors: [],
    advisory_notes: ''
  });

  // Load data when it changes
  useEffect(() => {
    if (data && data.exists !== false) {
      setFormData(prev => ({
        ...prev,
        ...Object.keys(data).reduce((acc, key) => {
          if (key !== 'id' && key !== 'user_id' && key !== 'created_at' && key !== 'updated_at') {
            acc[key] = data[key];
          }
          return acc;
        }, {})
      }));
    }
  }, [data]);

  // Load profile data (advisory team) when it changes
  useEffect(() => {
    if (profileData) {
      const parseJSON = (value) => {
        if (!value) return [];
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch {
            return [];
          }
        }
        return Array.isArray(value) ? value : [];
      };

      setAdvisoryTeam({
        has_attorney: profileData.has_attorney || false,
        attorney_name: profileData.attorney_name || '',
        attorney_email: profileData.attorney_email || '',
        attorney_phone: profileData.attorney_phone || '',
        has_accountant: profileData.has_accountant || false,
        accountant_name: profileData.accountant_name || '',
        accountant_email: profileData.accountant_email || '',
        accountant_phone: profileData.accountant_phone || '',
        has_financial_advisor: profileData.has_financial_advisor || false,
        financial_advisor_name: profileData.financial_advisor_name || '',
        financial_advisor_email: profileData.financial_advisor_email || '',
        financial_advisor_phone: profileData.financial_advisor_phone || '',
        has_exit_advisor: profileData.has_exit_advisor || false,
        exit_advisor_name: profileData.exit_advisor_name || '',
        exit_advisor_email: profileData.exit_advisor_email || '',
        exit_advisor_phone: profileData.exit_advisor_phone || '',
        has_insurance_agent: profileData.has_insurance_agent || false,
        insurance_agent_name: profileData.insurance_agent_name || '',
        insurance_agent_email: profileData.insurance_agent_email || '',
        insurance_agent_phone: profileData.insurance_agent_phone || '',
        has_banker: profileData.has_banker || false,
        banker_name: profileData.banker_name || '',
        banker_email: profileData.banker_email || '',
        banker_phone: profileData.banker_phone || '',
        has_estate_planner: profileData.has_estate_planner || false,
        estate_planner_name: profileData.estate_planner_name || '',
        estate_planner_email: profileData.estate_planner_email || '',
        estate_planner_phone: profileData.estate_planner_phone || '',
        has_business_coach: profileData.has_business_coach || false,
        business_coach_name: profileData.business_coach_name || '',
        business_coach_email: profileData.business_coach_email || '',
        business_coach_phone: profileData.business_coach_phone || '',
        custom_advisors: parseJSON(profileData.custom_advisors),
        advisory_notes: profileData.advisory_notes || ''
      });
    }
  }, [profileData]);

  // Formatting helpers for Section F
  const formatCurrencyInput = (value) => {
    if (!value) return '';
    // Remove non-numeric characters
    const num = parseFloat(value.toString().replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return '';
    // Format with commas, no decimals
    return '$' + num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const parseCurrencyInput = (value) => {
    if (!value) return '';
    // Remove all non-numeric characters except decimal and negative
    const cleaned = value.toString().replace(/[^0-9.-]/g, '');
    return cleaned;
  };

  const formatPercentageInput = (value) => {
    if (!value && value !== 0) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toFixed(1) + '%';
  };

  const parsePercentageInput = (value) => {
    if (!value) return '';
    // Remove % and any non-numeric except decimal and negative
    const cleaned = value.toString().replace(/[^0-9.-]/g, '');
    return cleaned;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  // Advisory Team handlers
  const handleAdvisoryTeamChange = (field, value) => {
    setAdvisoryTeam(prev => ({ ...prev, [field]: value }));
  };

  const addCustomAdvisor = () => {
    setAdvisoryTeam(prev => ({
      ...prev,
      custom_advisors: [...prev.custom_advisors, { title: '', name: '', email: '', phone: '' }]
    }));
  };

  const updateCustomAdvisor = (index, field, value) => {
    setAdvisoryTeam(prev => {
      const newAdvisors = [...prev.custom_advisors];
      newAdvisors[index][field] = value;
      return { ...prev, custom_advisors: newAdvisors };
    });
  };

  const removeCustomAdvisor = (index) => {
    setAdvisoryTeam(prev => {
      const newAdvisors = prev.custom_advisors.filter((_, i) => i !== index);
      return { ...prev, custom_advisors: newAdvisors };
    });
  };

  // Save advisory team changes to business profile
  const saveAdvisoryTeam = async () => {
    try {
      await onProfileSave(advisoryTeam);
    } catch (error) {
      console.error('[BusinessEnvisioning] Error saving advisory team:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleDownloadPDF = async () => {
    if (!currentBusinessId) {
      alert('No business selected. Please select a business first.');
      return;
    }

    setDownloading(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/business-envisioning/${currentBusinessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': currentBusinessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Business_Envisioning_Report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const showHelp = (title, content) => {
    setHelpModal({ isOpen: true, title, content });
  };

  const closeHelp = () => {
    setHelpModal({ isOpen: false, title: '', content: null });
  };

  // Section Header Component
  const SectionHeader = ({ icon: Icon, letter, title, description, helpContent }) => (
    <div className="flex items-start justify-between mb-6">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Icon className="w-5 h-5 text-indigo-600" />
          </div>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Section {letter}: {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-gray-600">{description}</p>
          )}
        </div>
      </div>
      {helpContent && (
        <button
          type="button"
          onClick={() => showHelp(`Section ${letter}: ${title}`, helpContent)}
          className="flex-shrink-0 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          title="Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      )}
    </div>
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-8 pb-8">
        {/* Quick Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-4 z-10">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Quick Navigation</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <a href="#section-a" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              A: Position
            </a>
            <a href="#section-b" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              B: Three Gaps
            </a>
            <a href="#section-c" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              C: 4 Capitals
            </a>
            <a href="#section-d" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              D: Purpose
            </a>
            <a href="#section-e" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              E: 10-Year Vision
            </a>
            <a href="#section-f" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              F: 3-5 Year Plan
            </a>
            <a href="#section-g" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              G: Owner Role
            </a>
            <a href="#section-h" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              H: Five Ds
            </a>
            <a href="#section-i" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              I: Exit Scenario
            </a>
            <a href="#section-j" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              J: Constraints
            </a>
            <a href="#section-k" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              K: 90-Day Focus
            </a>
            <a href="#section-l" className="text-xs px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-center font-medium">
              L: Advisory Team
            </a>
          </div>
        </div>

        {/* Section A: Current Business Position */}
        <div id="section-a" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Building}
            letter="A"
            title="Current Business Position"
            description="Start with where you are today"
            helpContent={
              <div className="space-y-3">
                <p>This section captures your current business reality as a baseline for planning. All assessment data is automatically populated from your Business Gap Assessment.</p>
                <p><strong>Gap Assessment Scores:</strong> Your Overall, Business, Family, Attractiveness, and Readiness scores are automatically pulled from your completed Business Gap Assessment. These provide a quantitative baseline of where your business stands today.</p>
                <p><strong>Top 3 Strengths:</strong> Automatically identified as your highest-scoring category areas from the assessment. These represent your competitive advantages and areas of excellence that can be leveraged in your exit strategy.</p>
                <p><strong>Top 3 Risks/Gaps:</strong> Automatically identified as your lowest-scoring category areas from the assessment. These are critical areas requiring attention and improvement before exit to maximize business value.</p>
                <p><strong>Business Model:</strong> Describe how your business creates and captures value. This is the only manual input field in this section and provides qualitative context for your quantitative scores.</p>
                <p className="text-xs text-gray-600 italic">Complete your Business Gap Assessment to see auto-populated scores, strengths, and risks.</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Assessment Gap Scores - Auto-populated from Business Gap Assessment */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Gap Assessment Scores</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto-populated from Business Gap Assessment</span>
              </div>

              {assessmentData && assessmentData.answered_questions > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {/* Overall Gap Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(gapScores.overallGapScore)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(gapScores.overallGapScore)}`}>
                      {formatPercentage(gapScores.overallGapScore)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Overall</p>
                  </div>

                  {/* Business Assessment Gap */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(gapScores.businessAssessmentGap)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(gapScores.businessAssessmentGap)}`}>
                      {formatPercentage(gapScores.businessAssessmentGap)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Business</p>
                  </div>

                  {/* Family Enterprise Gap */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(gapScores.familyEnterpriseGap)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(gapScores.familyEnterpriseGap)}`}>
                      {formatPercentage(gapScores.familyEnterpriseGap)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Family</p>
                  </div>

                  {/* Attractiveness Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(gapScores.attractivenessGap)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(gapScores.attractivenessGap)}`}>
                      {formatPercentage(gapScores.attractivenessGap)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Attractive</p>
                  </div>

                  {/* Readiness Score */}
                  <div className={`text-center p-4 rounded-lg border ${getScoreBgColor(gapScores.readinessGap)}`}>
                    <p className={`text-2xl font-bold ${getScoreColor(gapScores.readinessGap)}`}>
                      {formatPercentage(gapScores.readinessGap)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-semibold">Readiness</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                  <p className="text-gray-600 text-sm">
                    No assessment data available. Please complete the <strong>Business Gap Assessment</strong> to see your scores here.
                  </p>
                </div>
              )}
            </div>

            {/* Top Strengths - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Top 3 Strengths
                </label>
                <span className="text-xs text-gray-500 bg-green-50 px-2 py-1 rounded border border-green-200">Auto-populated</span>
              </div>
              {autoStrengths.length > 0 ? (
                <div className="space-y-3">
                  {autoStrengths.map((strength, index) => (
                    <div
                      key={index}
                      className={`px-4 py-3 rounded-lg border ${getScoreBgColor(strength.score)} border-gray-200`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                            {index + 1}
                          </div>
                          <div className="font-semibold text-gray-900 text-sm">{strength.name}</div>
                        </div>
                        <div className={`text-lg font-bold ml-3 ${getScoreColor(strength.score)}`}>
                          {formatPercentage(strength.score)}
                        </div>
                      </div>
                      <div className="ml-9 text-xs text-gray-600">{strength.summary}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">Complete assessment to see top strengths</div>
              )}
            </div>

            {/* Top Risks/Gaps - Auto-populated */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Top 3 Risks/Gaps
                </label>
                <span className="text-xs text-gray-500 bg-red-50 px-2 py-1 rounded border border-red-200">Auto-populated</span>
              </div>
              {autoRisks.length > 0 ? (
                <div className="space-y-3">
                  {autoRisks.map((risk, index) => (
                    <div
                      key={index}
                      className={`px-4 py-3 rounded-lg border ${getScoreBgColor(risk.score)} border-gray-200`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                            {index + 1}
                          </div>
                          <div className="font-semibold text-gray-900 text-sm">{risk.name}</div>
                        </div>
                        <div className={`text-lg font-bold ml-3 ${getScoreColor(risk.score)}`}>
                          {formatPercentage(risk.score)}
                        </div>
                      </div>
                      <div className="ml-9 text-xs text-gray-600">{risk.summary}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">Complete assessment to see top risks</div>
              )}
            </div>

            {/* Business Model Summary */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Business Model Summary
              </label>
              <textarea
                value={formData.business_model_summary}
                onChange={(e) => handleInputChange('business_model_summary', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Briefly describe how your business creates and captures value"
              />
            </div>
          </div>
        </div>

        {/* Section B: Three Gaps Analysis */}
        <div id="section-b" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={TrendingUp}
            letter="B"
            title="Three Gaps Analysis"
            description="Quantify the Value Gap, Profit Gap, and Wealth Gap"
            helpContent={
              <div className="space-y-3">
                <p><strong>Value Gap:</strong> Your value gap measures the additional business value you could unlock by improving your business gap assessment score.</p>
                <p><strong>Profit Gap:</strong> Your profit gap measures the additional business value you could unlock by improving your EBITDA margin to industry benchmark levels.</p>
                <p><strong>Wealth Gap:</strong> Your wealth gap measures the difference between your net worth (excluding business value) and the total capital needed to achieve financial independence after exiting your business.</p>
                <p>These three gaps are interconnected and form the foundation of your exit planning strategy.</p>
              </div>
            }
          />

          {/* Check if gap data exists */}
          {(valueGap || wealthGap || profitGap) ? (
            <div className="space-y-8">
              {/* Value Gap */}
              {valueGap && (
                <div className="bg-emerald-50 rounded-lg p-6 border-2 border-emerald-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Value Gap</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">Auto-populated from Valuation & Assessment</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Current State */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-2">Current State</p>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-700">Current Est. Business Exit Value:</span>
                        <span className="font-bold text-gray-900">{formatCurrency(valueGap.current_exit_value)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Current Overall Business Gap Score:</span>
                        <span className="font-semibold text-amber-600">{valueGap.current_attractiveness?.toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Green Zone Impact */}
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-xs font-semibold text-blue-900 mb-2">If at Green Zone ({valueGap.green_zone_attractiveness?.toFixed(1)}%)</p>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">Valuation Impact:</span>
                        <span className="font-bold text-blue-600 text-lg">{formatCurrency(valueGap.value_impact_green_zone)}</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Additional value from reaching green zone</p>
                    </div>

                    {/* Best in Class Impact */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <p className="text-xs font-semibold text-green-900 mb-2">If at Best in Class ({valueGap.best_in_class_attractiveness?.toFixed(1)}%)</p>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Valuation Impact:</span>
                        <span className="font-bold text-green-600 text-lg">{formatCurrency(valueGap.value_impact_best_in_class)}</span>
                      </div>
                      <p className="text-xs text-green-600 mt-2">Additional value from best in class</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Profit Gap */}
              {profitGap && (
                <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Profit Gap</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">Auto-populated from Valuation</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Current State */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-600 mb-2">Current State</p>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-700">Current Est. Business Exit Value:</span>
                        <span className="font-bold text-gray-900">{formatCurrency(profitGap.current_valuation)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Current EBITDA Margin:</span>
                        <span className="font-semibold text-amber-600">{profitGap.current_ebitda_margin?.toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Median Industry Margin Impact */}
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-xs font-semibold text-blue-900 mb-2">If at Median Industry Margin ({profitGap.industry_median_ebitda_margin?.toFixed(1)}%)</p>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">Valuation Impact:</span>
                        <span className="font-bold text-blue-600 text-lg">{formatCurrency(profitGap.valuation_impact_median)}</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">Additional value from median margin</p>
                    </div>

                    {/* Best in Class Margin Impact */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <p className="text-xs font-semibold text-green-900 mb-2">If at Best in Class Margin ({profitGap.industry_high_ebitda_margin?.toFixed(1)}%)</p>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Valuation Impact:</span>
                        <span className="font-bold text-green-600 text-lg">{formatCurrency(profitGap.valuation_impact_best)}</span>
                      </div>
                      <p className="text-xs text-green-600 mt-2">Additional value from best in class</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Wealth Gap */}
              {wealthGap && (
                <div className="bg-orange-50 rounded-lg p-6 border-2 border-orange-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Wealth Gap</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200">Auto-populated from Wealth Gap Calculator</span>
                  </div>

                  {/* Exit Year and Age */}
                  {wealthGap.exit_year && (
                    <div className="mb-4 bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <p className="text-base text-gray-700 text-center">
                        Exit Year: <span className="font-bold text-purple-800">{wealthGap.exit_year}</span>
                        {wealthGap.current_age && <> @ Age <span className="font-bold text-purple-800">{wealthGap.current_age + Math.max(0, wealthGap.exit_year - new Date().getFullYear())}</span></>}
                      </p>
                    </div>
                  )}

                  {/* Table Layout (matching Enhanced Dashboard) */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="grid grid-cols-12 gap-2 text-sm">
                      {/* Header Row */}
                      <div className="col-span-6 font-bold text-gray-900"></div>
                      <div className="col-span-3 font-bold text-gray-900 text-center">Current</div>
                      <div className="col-span-3 font-bold text-gray-900 text-center">At Exit</div>

                      {/* Net Monthly Needs */}
                      {wealthGap.wealth_goal_method === 'monthly_needs' && (
                        <>
                          <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                          <div className="col-span-6 text-gray-700 font-semibold">Net Monthly Needs</div>
                          <div className="col-span-3 text-center font-semibold text-blue-700">
                            {(() => {
                              const totalCurrentMonthlyNeed = (wealthGap.monthly_cash_need || 0) + (wealthGap.non_business_monthly_cash_needs || 0);
                              return formatCurrency(Math.max(0, totalCurrentMonthlyNeed - (wealthGap.outside_income_monthly || 0)));
                            })()}
                          </div>
                          <div className="col-span-3 text-center font-semibold text-blue-700">
                            {(() => {
                              // Calculate future net monthly need (matching WealthGapCard logic)
                              const currentYear = new Date().getFullYear();
                              const yearsUntilExit = Math.max(0, (wealthGap.exit_year || currentYear) - currentYear);
                              const totalCurrentMonthlyNeed = (wealthGap.monthly_cash_need || 0) + (wealthGap.non_business_monthly_cash_needs || 0);
                              const inflationMultiplier = Math.pow(1 + (wealthGap.annual_inflation_rate || 2) / 100, yearsUntilExit);
                              const monthlyNeedAtExit = totalCurrentMonthlyNeed * inflationMultiplier * (1 + (wealthGap.lifestyle_change_percentage || 0) / 100);
                              const outsideIncomeGrowthRate = (wealthGap.outside_income_annual_increase || 0) / 100;
                              const yearsUntilRetirement = Math.max(0, (wealthGap.retirement_age || wealthGap.current_age || 0) - (wealthGap.current_age || 0));
                              const futureOutsideIncomeMonthly = (wealthGap.outside_income_monthly || 0) * Math.pow(1 + outsideIncomeGrowthRate, yearsUntilRetirement);
                              const futureNetMonthlyNeed = Math.max(0, monthlyNeedAtExit - futureOutsideIncomeMonthly);
                              return formatCurrency(futureNetMonthlyNeed);
                            })()}
                          </div>
                        </>
                      )}

                      {/* Total Capital Needed */}
                      <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                      <div className="col-span-6 text-gray-700 font-semibold">Total Capital Needed</div>
                      <div className="col-span-3 text-center text-gray-500">—</div>
                      <div className="col-span-3 text-center font-semibold text-indigo-700">
                        {wealthGap.required_sale_price_data?.total_capital_needed !== undefined
                          ? formatCurrency(wealthGap.required_sale_price_data.total_capital_needed)
                          : formatCurrency(wealthGap.calculated_wealth_goal)}
                      </div>

                      {/* Net Worth */}
                      <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                      <div className="col-span-6 text-gray-700 font-semibold">Net Worth</div>
                      <div className="col-span-3 text-center font-semibold text-emerald-700">
                        {formatCurrency(wealthGap.current_net_worth)}
                      </div>
                      <div className="col-span-3 text-center font-semibold text-emerald-700">
                        {wealthGap.required_sale_price_data?.future_net_worth !== undefined
                          ? formatCurrency(wealthGap.required_sale_price_data.future_net_worth)
                          : '—'}
                      </div>

                      {/* Estimated Business Exit Value */}
                      <div className="col-span-12 border-t border-gray-300 mt-1 pt-2"></div>
                      <div className="col-span-6 text-gray-700 font-semibold">Est. Business Exit Value</div>
                      <div className="col-span-3 text-center font-semibold text-cyan-700">
                        {formatCurrency(wealthGap.desired_sale_price || 0)}
                      </div>
                      <div className="col-span-3 text-center font-semibold text-cyan-700">
                        {formatCurrency(wealthGap.future_business_value || wealthGap.desired_sale_price || 0)}
                      </div>
                    </div>
                  </div>

                  {/* Required from Business Sale or Wealth Surplus Summary */}
                  {wealthGap.wealth_goal_method === 'monthly_needs' && wealthGap.required_sale_price_data?.total_capital_needed !== undefined && (
                    <div className={`mt-4 rounded-lg p-3 border-2 ${
                      wealthGap.required_sale_price_data.wealth_surplus > 0
                        ? 'bg-green-100 border-green-300'
                        : 'bg-orange-100 border-orange-300'
                    }`}>
                      {wealthGap.required_sale_price_data.wealth_surplus > 0 ? (
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-green-900">Wealth Surplus</p>
                          <p className="text-xl font-bold text-green-700">
                            {formatCurrency(wealthGap.required_sale_price_data.wealth_surplus)}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-orange-900">Required from Business Sale</p>
                          <p className="text-xl font-bold text-orange-700">
                            {formatCurrency(Math.abs(wealthGap.required_sale_price_data.wealth_surplus))}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-gray-200">
              <p className="text-gray-600 mb-4">
                No gap data available yet. Complete the following to see your Three Gaps Analysis:
              </p>
              <ul className="text-sm text-gray-700 space-y-2 max-w-md mx-auto text-left">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Valuation:</strong> Calculate your business value</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Wealth Gap Calculator:</strong> Define your financial independence goal</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">•</span>
                  <span><strong>Business Gap Assessment:</strong> Complete your assessment</span>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Section C: Four Intangible Capitals (4Cs) */}
        <div id="section-c" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Users}
            letter="C"
            title="Four Intangible Capitals (4Cs)"
            description="Current ratings auto-populated from assessment; describe your 3-5 year vision for each"
            helpContent={
              <div className="space-y-3">
                <p>The Four Intangible Capitals are the key drivers of business value:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Human Capital:</strong> Your team's skills, knowledge, and capabilities</li>
                  <li><strong>Structural Capital:</strong> Systems, processes, IP, and infrastructure</li>
                  <li><strong>Customer Capital:</strong> Relationships, brand, and market position</li>
                  <li><strong>Social Capital:</strong> Community reputation and network effects</li>
                </ul>
                <p>Current ratings are calculated from your Business Gap Assessment responses. Describe your 3-5 year vision for each capital area.</p>
              </div>
            }
          />

          <div className="space-y-8">
            {/* Human Capital */}
            <div className="border-l-4 border-indigo-500 pl-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Human Capital</h3>
              <div className="space-y-4">
                {intangibleScores.Human > 0 ? (
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-4">
                        <div className={`text-3xl font-bold ${getScoreColor(intangibleScores.Human)}`}>
                          {formatPercentage(intangibleScores.Human)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">
                            The collective skills, knowledge, and capabilities of your workforce. This includes employee expertise, training programs, leadership quality, and organizational culture that drives performance.
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200 whitespace-nowrap ml-3">Auto-populated</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                    <p className="text-sm text-gray-600">Complete assessment to see rating</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3-5 Year Vision
                  </label>
                  <textarea
                    value={formData.human_capital_vision}
                    onChange={(e) => handleInputChange('human_capital_vision', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="Describe your ideal team capabilities, culture, and leadership structure"
                  />
                </div>
              </div>
            </div>

            {/* Structural Capital */}
            <div className="border-l-4 border-purple-500 pl-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Structural Capital</h3>
              <div className="space-y-4">
                {intangibleScores.Structural > 0 ? (
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-4">
                        <div className={`text-3xl font-bold ${getScoreColor(intangibleScores.Structural)}`}>
                          {formatPercentage(intangibleScores.Structural)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">
                            The systems, processes, databases, and intellectual property that remain with the company. This includes proprietary software, documented procedures, patents, and organizational infrastructure.
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200 whitespace-nowrap ml-3">Auto-populated</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                    <p className="text-sm text-gray-600">Complete assessment to see rating</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3-5 Year Vision
                  </label>
                  <textarea
                    value={formData.structural_capital_vision}
                    onChange={(e) => handleInputChange('structural_capital_vision', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    placeholder="Describe your ideal systems, processes, IP, and infrastructure"
                  />
                </div>
              </div>
            </div>

            {/* Customer Capital */}
            <div className="border-l-4 border-blue-500 pl-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Capital</h3>
              <div className="space-y-4">
                {intangibleScores.Customer > 0 ? (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-4">
                        <div className={`text-3xl font-bold ${getScoreColor(intangibleScores.Customer)}`}>
                          {formatPercentage(intangibleScores.Customer)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">
                            The value of your customer relationships, brand reputation, and market position. This includes customer loyalty, retention rates, brand equity, and the strength of customer relationships.
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200 whitespace-nowrap ml-3">Auto-populated</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                    <p className="text-sm text-gray-600">Complete assessment to see rating</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3-5 Year Vision
                  </label>
                  <textarea
                    value={formData.customer_capital_vision}
                    onChange={(e) => handleInputChange('customer_capital_vision', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Describe your ideal customer relationships, brand strength, and market position"
                  />
                </div>
              </div>
            </div>

            {/* Social Capital */}
            <div className="border-l-4 border-green-500 pl-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Capital</h3>
              <div className="space-y-4">
                {intangibleScores.Social > 0 ? (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-4">
                        <div className={`text-3xl font-bold ${getScoreColor(intangibleScores.Social)}`}>
                          {formatPercentage(intangibleScores.Social)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">
                            The value derived from networks, partnerships, and external relationships. This includes strategic alliances, supplier relationships, industry connections, and community reputation.
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border border-gray-200 whitespace-nowrap ml-3">Auto-populated</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                    <p className="text-sm text-gray-600">Complete assessment to see rating</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    3-5 Year Vision
                  </label>
                  <textarea
                    value={formData.social_capital_vision}
                    onChange={(e) => handleInputChange('social_capital_vision', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                    placeholder="Describe your ideal community reputation, network, and social impact"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section D: Purpose and Vision */}
        <div id="section-d" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Target}
            letter="D"
            title="Purpose and Vision"
            description="Define why your business exists and where it's going"
            helpContent={
              <div className="space-y-3">
                <p>This section captures the foundational elements that guide your business:</p>
                <p><strong>Purpose Statement:</strong> Why does this business exist beyond making money?</p>
                <p><strong>Vision:</strong> What does the future look like when you achieve your purpose?</p>
                <p><strong>Mission:</strong> How will you achieve your vision? What do you do day-to-day?</p>
                <p><strong>Kingdom Impact Goals:</strong> How will your business advance God's kingdom and serve others?</p>
                <p><strong>Core Values:</strong> The principles that guide decisions and behavior (up to 10, progressively revealed).</p>
                <p><strong>Business Owner Type:</strong> Are you building for lifestyle (cash flow, flexibility) or value creation (growth, eventual sale)?</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Purpose Statement
              </label>
              <textarea
                value={formData.purpose_statement}
                onChange={(e) => handleInputChange('purpose_statement', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What is this business ultimately for? Why does it exist?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Vision Statement
              </label>
              <textarea
                value={formData.vision_statement}
                onChange={(e) => handleInputChange('vision_statement', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What does the future look like when you achieve your purpose?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mission Statement
              </label>
              <textarea
                value={formData.mission_statement}
                onChange={(e) => handleInputChange('mission_statement', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="How will you achieve your vision? What do you do day-to-day?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason to Believe
              </label>
              <p className="text-xs text-gray-500 mb-2">Why do you believe you can achieve this vision?</p>
              <textarea
                value={formData.reason_to_believe}
                onChange={(e) => handleInputChange('reason_to_believe', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What capabilities, advantages, or insights give you confidence?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Proof Points
              </label>
              <p className="text-xs text-gray-500 mb-2">What evidence supports your reason to believe?</p>
              <textarea
                value={formData.proof_points}
                onChange={(e) => handleInputChange('proof_points', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Track record, case studies, testimonials, metrics, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tone & Manner
              </label>
              <p className="text-xs text-gray-500 mb-2">What is the experience going to be like?</p>
              <textarea
                value={formData.tone_manner}
                onChange={(e) => handleInputChange('tone_manner', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Describe the customer experience, company culture, and brand personality"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Kingdom Impact Goals
              </label>
              <textarea
                value={formData.kingdom_impact_goals}
                onChange={(e) => handleInputChange('kingdom_impact_goals', e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="How will this business impact employees, customers, community, and the kingdom?"
              />
            </div>

            {/* Core Values */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Core Values
              </label>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                  // Show first 3 rows always, then show next row if previous row has data
                  const shouldShow = num <= 3 ||
                    (formData[`core_value_${num - 1}`] || formData[`core_value_${num - 1}_applied`]);

                  if (!shouldShow) return null;

                  return (
                    <div key={num} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          value={formData[`core_value_${num}`]}
                          onChange={(e) => handleInputChange(`core_value_${num}`, e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          placeholder={`Core Value ${num}`}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={formData[`core_value_${num}_applied`]}
                          onChange={(e) => handleInputChange(`core_value_${num}_applied`, e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          placeholder="How It's Applied"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Business Owner Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Business Owner Type
              </label>
              <p className="text-xs text-gray-500 mb-3">Are you building for lifestyle or value?</p>
              <div className="space-y-3">
                <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                  <input
                    type="radio"
                    name="business_owner_type"
                    value="lifestyle"
                    checked={formData.business_owner_type === 'lifestyle'}
                    onChange={(e) => handleInputChange('business_owner_type', e.target.value)}
                    className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Lifestyle Business Owner</span>
                    <p className="text-xs text-gray-600 mt-1">Prioritize cash flow, flexibility, and personal lifestyle. The business supports your desired way of life.</p>
                  </div>
                </label>
                <label className="flex items-start p-4 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                  <input
                    type="radio"
                    name="business_owner_type"
                    value="value_building"
                    checked={formData.business_owner_type === 'value_building'}
                    onChange={(e) => handleInputChange('business_owner_type', e.target.value)}
                    className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Value Building Business Owner</span>
                    <p className="text-xs text-gray-600 mt-1">Prioritize growth and increasing enterprise value. Building toward an eventual exit or sale.</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Section E: 10-Year Kingdom Vision */}
        <div id="section-e" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Target}
            letter="E"
            title="10-Year Kingdom Vision"
            description="Paint a picture of where you're headed long-term"
            helpContent={
              <div className="space-y-3">
                <p>Think 10 years out - what does success look like for your business?</p>
                <p>Define your vision in terms of size, profitability, enterprise value, and your role as the owner.</p>
                <p>This long-term vision should align with your Purpose and Vision (Section D) and inform your near-term targets (Section F).</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  10-Year Size/Scale
                </label>
                <input
                  type="text"
                  value={formData.ten_year_size}
                  onChange={(e) => handleInputChange('ten_year_size', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., $50M revenue, 200 employees"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  10-Year Profitability
                </label>
                <input
                  type="text"
                  value={formData.ten_year_profitability}
                  onChange={(e) => handleInputChange('ten_year_profitability', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., 20% EBITDA margin"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  10-Year Business Value
                </label>
                <input
                  type="text"
                  value={formData.ten_year_business_value}
                  onChange={(e) => handleInputChange('ten_year_business_value', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., $40M enterprise value"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  10-Year Owner Role
                </label>
                <input
                  type="text"
                  value={formData.ten_year_owner_role}
                  onChange={(e) => handleInputChange('ten_year_owner_role', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., Board chair, fully exited, passive owner"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section F: 3-5 Year Business Picture */}
        <div id="section-f" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={DollarSign}
            letter="F"
            title="3-5 Year Business Picture"
            description="Define your near-term business targets"
            helpContent={
              <div className="space-y-3">
                <p>This section brings your 10-year vision into sharper focus for the next 3-5 years.</p>
                <p>Define specific targets for market positioning, financial performance, and business value.</p>
                <p><strong>SWOT Analysis:</strong> Conduct a strategic assessment of your business by identifying internal Strengths and Weaknesses, along with external Opportunities and Threats. This analysis helps inform your strategic priorities and action plans.</p>
                <p>These targets should bridge the gap between where you are today (Section A) and where you want to be long-term (Section D).</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Target Customers
              </label>
              <textarea
                value={formData.target_customers}
                onChange={(e) => handleInputChange('target_customers', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Who are you serving? What is your ideal customer profile?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Market Reputation
              </label>
              <textarea
                value={formData.market_reputation}
                onChange={(e) => handleInputChange('market_reputation', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What do you want to be known for in your market?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Competitive Position
              </label>
              <textarea
                value={formData.competitive_position}
                onChange={(e) => handleInputChange('competitive_position', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="How do you stand out from competitors?"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  3-Year Revenue Target
                </label>
                <input
                  type="text"
                  value={formatCurrencyInput(formData.three_year_revenue)}
                  onChange={(e) => handleInputChange('three_year_revenue', parseCurrencyInput(e.target.value))}
                  onBlur={(e) => {
                    const formatted = formatCurrencyInput(formData.three_year_revenue);
                    e.target.value = formatted;
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="$1,000,000"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  3-Year EBITDA Margin (%)
                </label>
                <input
                  type="text"
                  value={formData.three_year_ebitda_margin ? formData.three_year_ebitda_margin + '%' : ''}
                  onChange={(e) => {
                    const value = parsePercentageInput(e.target.value);
                    handleInputChange('three_year_ebitda_margin', value);
                  }}
                  onBlur={(e) => {
                    if (formData.three_year_ebitda_margin) {
                      const num = parseFloat(formData.three_year_ebitda_margin);
                      if (!isNaN(num)) {
                        handleInputChange('three_year_ebitda_margin', num.toFixed(1));
                      }
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="25.5%"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recurring Revenue (%)
                </label>
                <input
                  type="text"
                  value={formData.three_year_recurring_revenue_pct ? formData.three_year_recurring_revenue_pct + '%' : ''}
                  onChange={(e) => {
                    const value = parsePercentageInput(e.target.value);
                    handleInputChange('three_year_recurring_revenue_pct', value);
                  }}
                  onBlur={(e) => {
                    if (formData.three_year_recurring_revenue_pct) {
                      const num = parseFloat(formData.three_year_recurring_revenue_pct);
                      if (!isNaN(num)) {
                        handleInputChange('three_year_recurring_revenue_pct', num.toFixed(1));
                      }
                    }
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="75.0%"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  3-Year Value Estimate (Min)
                </label>
                <input
                  type="text"
                  value={formatCurrencyInput(formData.three_year_value_min)}
                  onChange={(e) => handleInputChange('three_year_value_min', parseCurrencyInput(e.target.value))}
                  onBlur={(e) => {
                    const formatted = formatCurrencyInput(formData.three_year_value_min);
                    e.target.value = formatted;
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="$3,000,000"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  3-Year Value Estimate (Max)
                </label>
                <input
                  type="text"
                  value={formatCurrencyInput(formData.three_year_value_max)}
                  onChange={(e) => handleInputChange('three_year_value_max', parseCurrencyInput(e.target.value))}
                  onBlur={(e) => {
                    const formatted = formatCurrencyInput(formData.three_year_value_max);
                    e.target.value = formatted;
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="$5,000,000"
                />
              </div>
            </div>

            {/* SWOT Analysis */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                SWOT Analysis
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Strengths
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Internal advantages and capabilities</p>
                  <textarea
                    value={formData.swot_strengths}
                    onChange={(e) => handleInputChange('swot_strengths', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="What does your business do well? What unique resources or capabilities do you have?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weaknesses
                  </label>
                  <p className="text-xs text-gray-500 mb-2">Internal limitations and areas for improvement</p>
                  <textarea
                    value={formData.swot_weaknesses}
                    onChange={(e) => handleInputChange('swot_weaknesses', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="What could be improved? What resources are you lacking?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opportunities
                  </label>
                  <p className="text-xs text-gray-500 mb-2">External factors that could benefit your business</p>
                  <textarea
                    value={formData.swot_opportunities}
                    onChange={(e) => handleInputChange('swot_opportunities', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="What market trends favor you? What opportunities exist for growth?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Threats
                  </label>
                  <p className="text-xs text-gray-500 mb-2">External challenges and risks</p>
                  <textarea
                    value={formData.swot_threats}
                    onChange={(e) => handleInputChange('swot_threats', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="What obstacles do you face? What could hurt your business?"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section G: Owner Role & Dependency Reduction */}
        <div id="section-g" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Users}
            letter="G"
            title="Owner Role & Dependency Reduction"
            description="Plan how to make yourself optional"
            helpContent={
              <div className="space-y-3">
                <p>A business that depends on the owner is worth less and harder to exit.</p>
                <p><strong>The goal:</strong> Build a business that can thrive without you in day-to-day operations.</p>
                <p><strong>Key question:</strong> Could your business run successfully for 3-6 months without you?</p>
                <p>Identify which responsibilities to keep (strategic) vs. delegate (operational).</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Responsibilities to Keep (select strategic roles)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['Strategy & Vision', 'Board Leadership', 'Key Relationships', 'Culture Champion', 'M&A / Strategic Deals', 'Investor Relations'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.responsibilities_keep.includes(option)}
                      onChange={() => handleCheckboxChange('responsibilities_keep', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Responsibilities to Delegate or Eliminate
              </label>
              <textarea
                value={formData.responsibilities_delegate}
                onChange={(e) => handleInputChange('responsibilities_delegate', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="List operational tasks you need to delegate (e.g., daily operations, financial management, sales execution, technical work)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sabbatical Readiness
              </label>
              <textarea
                value={formData.sabbatical_readiness}
                onChange={(e) => handleInputChange('sabbatical_readiness', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Could the business run for 3-6 months without you? What needs to change to make that possible?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Key Person Risk Mitigation
              </label>
              <textarea
                value={formData.key_person_risk}
                onChange={(e) => handleInputChange('key_person_risk', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What would give a buyer confidence that the business doesn't depend on you?"
              />
            </div>
          </div>
        </div>

        {/* Section H: Preparing for the Five Ds */}
        <div id="section-h" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Shield}
            letter="H"
            title="Preparing for the Five Ds"
            description="Death, Disability, Divorce, Distress, Disagreement"
            helpContent={
              <div className="space-y-3">
                <p>The Five Ds are life events that force an exit on your terms or not:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Death:</strong> Estate planning, life insurance, succession readiness</li>
                  <li><strong>Disability:</strong> Key person insurance, business continuity plans</li>
                  <li><strong>Divorce:</strong> Shareholder agreements, business valuation protection</li>
                  <li><strong>Distress:</strong> Financial reserves, crisis management plans</li>
                  <li><strong>Disagreement:</strong> Partner buyout provisions, dispute resolution</li>
                </ul>
                <p>For each of the Five Ds, check off the preparedness items you have in place and add any additional notes about your current status and next steps.</p>
                <p>Identify your top priority actions to improve preparedness across all five areas.</p>
              </div>
            }
          />

          <div className="space-y-6">
            {/* Death */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Death (Estate planning, life insurance, succession)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Life insurance in place', 'Estate plan current', 'Succession plan documented', 'Buy-sell agreement signed', 'Beneficiary designations updated', 'Will and trusts established'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_death_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_death_items', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_death}
                onChange={(e) => handleInputChange('five_ds_death', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Disability */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Disability (Key person insurance, continuity plans)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Disability insurance in place', 'Key person insurance coverage', 'Business continuity plan documented', 'Backup leadership identified', 'Emergency procedures established', 'Cross-training program active'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_disability_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_disability_items', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_disability}
                onChange={(e) => handleInputChange('five_ds_disability', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Divorce */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Divorce (Shareholder agreements, valuation protection)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Prenuptial/postnuptial agreement in place', 'Shareholder agreement protects business', 'Recent business valuation completed', 'Ownership structure documented', 'Separate property designations clear', 'Family council or governance structure'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_divorce_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_divorce_items', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_divorce}
                onChange={(e) => handleInputChange('five_ds_divorce', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Distress */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Distress (Financial reserves, crisis management)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Emergency fund established (6-12 months)', 'Line of credit available', 'Crisis management plan documented', 'Financial stress testing completed', 'Diversified revenue streams', 'Insurance coverage adequate'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_distress_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_distress_items', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_distress}
                onChange={(e) => handleInputChange('five_ds_distress', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            {/* Disagreement */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Disagreement (Partner buyout provisions, dispute resolution)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {['Buy-sell agreement in place', 'Dispute resolution process documented', 'Arbitration clause included', 'Valuation method agreed upon', 'Decision-making authority clear', 'Regular partner/board meetings held'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.five_ds_disagreement_items.includes(option)}
                      onChange={() => handleCheckboxChange('five_ds_disagreement_items', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData.five_ds_disagreement}
                onChange={(e) => handleInputChange('five_ds_disagreement', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Rate preparedness (1-10) and describe current status"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Priority Actions for Five Ds
              </label>
              <textarea
                value={formData.five_ds_priorities}
                onChange={(e) => handleInputChange('five_ds_priorities', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What are your top 3 priorities to improve preparedness?"
              />
            </div>
          </div>
        </div>

        {/* Section I: Ideal Exit Scenario */}
        <div id="section-i" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Target}
            letter="I"
            title="Ideal Exit Scenario"
            description="Define your preferred exit path and non-negotiables"
            helpContent={
              <div className="space-y-3">
                <p>Clarity on your ideal exit helps you make better decisions today.</p>
                <p><strong>Consider:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>When do you want to exit?</li>
                  <li>To whom? (Strategic buyer, PE, management, family)</li>
                  <li>What deal structure? (Cash upfront, earnout, seller note)</li>
                  <li>What role post-exit? (Clean break, advisor, board seat)</li>
                  <li>What are your red lines? (Things you'll never accept)</li>
                </ul>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Exit Timing Window
              </label>
              <input
                type="text"
                value={formData.exit_timing_window}
                onChange={(e) => handleInputChange('exit_timing_window', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="e.g., 3-5 years, 2027-2030, when value hits $50M"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Exit Path Preferences (select all that interest you)
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/exit-strategy-quiz')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
                >
                  <ClipboardList size={16} />
                  Take Exit Quiz
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Not sure? Take the quiz to help identify your ideal exit path.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {['Strategic Sale', 'Private Equity Sale', 'Family Succession', 'Management Buyout', 'ESOP', 'IPO', 'Gradual Wind Down', 'Undecided'].map((option) => (
                  <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.exit_path_preferences.includes(option)}
                      onChange={() => handleCheckboxChange('exit_path_preferences', option)}
                      className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Preferred Deal Structure
              </label>
              <textarea
                value={formData.exit_deal_structure}
                onChange={(e) => handleInputChange('exit_deal_structure', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="e.g., 80% cash at close, 20% earnout over 2 years, no seller note"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Post-Exit Transition Role
              </label>
              <textarea
                value={formData.exit_transition_role}
                onChange={(e) => handleInputChange('exit_transition_role', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="e.g., 6-month transition, then board advisor role, or clean exit day 1"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Red Lines (Non-Negotiables)
              </label>
              <textarea
                value={formData.exit_red_lines}
                onChange={(e) => handleInputChange('exit_red_lines', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What would you never accept in an exit? (e.g., mass layoffs, moving operations, buyer with bad reputation)"
              />
            </div>
          </div>
        </div>

        {/* Section J: Constraints, Risks & Assumptions */}
        <div id="section-j" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={AlertCircle}
            letter="J"
            title="Constraints, Risks & Assumptions"
            description="Identify what could accelerate or delay your plan"
            helpContent={
              <div className="space-y-3">
                <p>Every plan has constraints, risks, and assumptions that need to be surfaced.</p>
                <p><strong>Constraints:</strong> Real limitations (financial, operational, personal) that constrain your options.</p>
                <p><strong>Assumptions:</strong> Things you're counting on being true (market growth, key employee retention, etc.).</p>
                <p><strong>Acceleration/Delay Factors:</strong> What could speed up or slow down your exit timeline?</p>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Top 3 Constraints
              </label>
              <div className="space-y-3">
                {[0, 1, 2].map((index) => (
                  <input
                    key={index}
                    type="text"
                    value={formData.constraints[index]}
                    onChange={(e) => handleArrayChange('constraints', index, e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder={`Constraint ${index + 1} (e.g., limited working capital, key person dependency, debt covenants)`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Key Assumptions
              </label>
              <textarea
                value={formData.key_assumptions}
                onChange={(e) => handleInputChange('key_assumptions', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What must be true for this plan to work? (e.g., market continues growing, team stays intact, no major recession)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acceleration Factors
              </label>
              <textarea
                value={formData.acceleration_factors}
                onChange={(e) => handleInputChange('acceleration_factors', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What could speed up your exit? (e.g., unsolicited offer, market consolidation, breakthrough product)"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Delay Factors
              </label>
              <textarea
                value={formData.delay_factors}
                onChange={(e) => handleInputChange('delay_factors', e.target.value)}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="What could delay your exit? (e.g., economic downturn, loss of key customer, regulatory changes)"
              />
            </div>
          </div>
        </div>

        {/* Section K: 90-Day Strategic Focus */}
        <div id="section-k" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Calendar}
            letter="K"
            title="90-Day Strategic Focus"
            description="What are you doing in the next 90 days to accelerate value?"
            helpContent={
              <div className="space-y-3">
                <p>Long-term vision without near-term action is just a dream.</p>
                <p><strong>Focus on:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li><strong>Quick Wins:</strong> Small actions that create immediate value</li>
                  <li><strong>Longer-Term Projects:</strong> Strategic initiatives that take time but have big impact</li>
                  <li><strong>Value Acceleration Priorities:</strong> Which of the Four Capitals (Human, Structural, Customer, Social) needs the most attention right now?</li>
                </ul>
              </div>
            }
          />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Quick Wins (Next 90 Days)
              </label>
              <div className="space-y-3">
                {[0, 1, 2].map((index) => (
                  <input
                    key={index}
                    type="text"
                    value={formData.quick_wins[index]}
                    onChange={(e) => handleArrayChange('quick_wins', index, e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder={`Quick Win ${index + 1} (small action, immediate impact)`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Longer-Term Projects (3-12 months)
              </label>
              <div className="space-y-3">
                {[0, 1, 2].map((index) => (
                  <input
                    key={index}
                    type="text"
                    value={formData.longer_term_projects[index]}
                    onChange={(e) => handleArrayChange('longer_term_projects', index, e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder={`Project ${index + 1} (strategic initiative with big impact)`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Value Acceleration Priorities
              </label>
              <textarea
                value={formData.value_acceleration_priorities}
                onChange={(e) => handleInputChange('value_acceleration_priorities', e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Which of the Four Capitals needs the most attention? What specific actions will you take?"
              />
            </div>
          </div>
        </div>

        {/* Section L: Advisory Team Alignment */}
        <div id="section-l" className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 scroll-mt-32">
          <SectionHeader
            icon={Users}
            letter="L"
            title="Advisory Team Alignment"
            description="Who is on your team and what gaps exist?"
            helpContent={
              <div className="space-y-3">
                <p>You can't do this alone. Building business value and planning an exit requires a strong advisory team.</p>
                <p><strong>Key roles might include:</strong></p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Exit Planning Advisor (CEPA)</li>
                  <li>M&A Advisor / Investment Banker</li>
                  <li>CPA / Tax Advisor</li>
                  <li>Attorney (business/M&A)</li>
                  <li>Wealth Manager</li>
                  <li>Insurance Specialist</li>
                  <li>Executive Coach / Peer Group</li>
                </ul>
              </div>
            }
          />

          <p className="text-sm text-gray-600 mb-4">Select advisors on your team and provide their contact information. Changes here are automatically synced with your Client Profile.</p>
          <div className="space-y-6">
            {[
              { key: 'attorney', label: 'Business Attorney' },
              { key: 'accountant', label: 'CPA / Accountant' },
              { key: 'financial_advisor', label: 'Financial Advisor / Wealth Manager' },
              { key: 'exit_advisor', label: 'Exit / M&A Advisor' },
              { key: 'insurance_agent', label: 'Insurance Agent / Risk Advisor' },
              { key: 'banker', label: 'Banker / Lender' },
              { key: 'estate_planner', label: 'Estate Planner / Tax Attorney' },
              { key: 'business_coach', label: 'Business Coach / Consultant' }
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="flex items-center cursor-pointer group mb-2">
                  <input
                    type="checkbox"
                    checked={advisoryTeam[`has_${key}`]}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      handleAdvisoryTeamChange(`has_${key}`, isChecked);
                      // Only auto-save when unchecking (removing an advisor)
                      if (!isChecked) {
                        setTimeout(saveAdvisoryTeam, 100);
                      }
                    }}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">{label}</span>
                </label>
                {advisoryTeam[`has_${key}`] && (
                  <div className="ml-6 grid grid-cols-1 md:grid-cols-3 gap-3 mt-2 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <input
                      type="text"
                      value={advisoryTeam[`${key}_name`]}
                      onChange={(e) => handleAdvisoryTeamChange(`${key}_name`, e.target.value)}
                      onBlur={saveAdvisoryTeam}
                      placeholder="Name"
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <input
                      type="email"
                      value={advisoryTeam[`${key}_email`]}
                      onChange={(e) => handleAdvisoryTeamChange(`${key}_email`, e.target.value)}
                      onBlur={saveAdvisoryTeam}
                      placeholder="Email"
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                    <input
                      type="tel"
                      value={advisoryTeam[`${key}_phone`]}
                      onChange={(e) => handleAdvisoryTeamChange(`${key}_phone`, e.target.value)}
                      onBlur={saveAdvisoryTeam}
                      placeholder="Phone"
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Custom Advisors */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-800">Custom Advisors</h3>
                <button
                  type="button"
                  onClick={addCustomAdvisor}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition text-sm font-medium"
                >
                  <Plus size={16} />
                  Add Custom Advisor
                </button>
              </div>
              {advisoryTeam.custom_advisors.length > 0 && (
                <div className="space-y-4">
                  {advisoryTeam.custom_advisors.map((advisor, index) => (
                    <div key={index} className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-indigo-900">Custom Advisor {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => { removeCustomAdvisor(index); saveAdvisoryTeam(); }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input
                          type="text"
                          value={advisor.title}
                          onChange={(e) => updateCustomAdvisor(index, 'title', e.target.value)}
                          onBlur={saveAdvisoryTeam}
                          placeholder="Title/Role"
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="text"
                          value={advisor.name}
                          onChange={(e) => updateCustomAdvisor(index, 'name', e.target.value)}
                          onBlur={saveAdvisoryTeam}
                          placeholder="Name"
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="email"
                          value={advisor.email}
                          onChange={(e) => updateCustomAdvisor(index, 'email', e.target.value)}
                          onBlur={saveAdvisoryTeam}
                          placeholder="Email"
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="tel"
                          value={advisor.phone}
                          onChange={(e) => updateCustomAdvisor(index, 'phone', e.target.value)}
                          onBlur={saveAdvisoryTeam}
                          placeholder="Phone"
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Advisory Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Advisory Team Notes
              </label>
              <textarea
                value={advisoryTeam.advisory_notes}
                onChange={(e) => handleAdvisoryTeamChange('advisory_notes', e.target.value)}
                onBlur={saveAdvisoryTeam}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Additional notes about your advisory team, gaps, or needs..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-4 sticky bottom-0 bg-white border-t border-gray-200 pt-6 pb-2">
          {/* Save Status */}
          {saveStatus === 'success' && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Saved successfully!</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Error saving. Please try again.</span>
            </div>
          )}
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            {downloading ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Business Envisioning'}
          </button>
        </div>
      </form>

      {/* Help Modal */}
      <HelpModal
        isOpen={helpModal.isOpen}
        onClose={closeHelp}
        title={helpModal.title}
      >
        {helpModal.content}
      </HelpModal>
    </>
  );
};

export default BusinessEnvisioning;
